// By-location table (flat networks only): no transfer lanes exist, so the
// lever is purchasing. Sortable, with a bold Network total row under a 2px
// rule. Δ target is colored (within 0.5 neutral, under red, over orange) and
// the At-risk cell deep-links into Inventory Health.
import { useMemo, useState } from "react";
import {
  SnapshotInbound,
  SnapshotLocation,
  SnapshotPeriod,
  SUPPLY_ABBREV,
} from "./types";
import {
  EM_DASH,
  LinkVal,
  SnapCard,
  fmtSigned,
  un,
  usd,
  useSnapshotTokens,
} from "./primitives";

type SortKey =
  | "name"
  | "units"
  | "value"
  | "wos"
  | "targetWos"
  | "deltaTarget"
  | "inStock"
  | "inbound"
  | "nextArrival"
  | "atRisk";

const columnsFor = (
  period: SnapshotPeriod
): { key: SortKey; label: string; numeric: boolean }[] => [
  { key: "name", label: "Location", numeric: false },
  { key: "units", label: "On hand", numeric: true },
  { key: "value", label: "Value", numeric: true },
  { key: "wos", label: SUPPLY_ABBREV[period], numeric: true },
  { key: "targetWos", label: "Target", numeric: true },
  { key: "deltaTarget", label: "Δ target", numeric: true },
  { key: "inStock", label: "In stock", numeric: true },
  { key: "inbound", label: "Inbound", numeric: true },
  { key: "nextArrival", label: "Next arrival", numeric: true },
  { key: "atRisk", label: "SKUs at risk", numeric: true },
];

const sortValue = (
  l: SnapshotLocation,
  key: SortKey
): string | number | null => {
  if (key === "name") {
    return l.name;
  }
  if (key === "deltaTarget") {
    return l.wos != null && l.targetWos != null ? l.wos - l.targetWos : null;
  }
  if (key === "nextArrival") {
    return l.nextArrival;
  }
  return l[key];
};

export const LocationTable = ({
  locations,
  inbound,
  inventoryHealthPath,
  period,
}: {
  locations: SnapshotLocation[];
  inbound: SnapshotInbound;
  inventoryHealthPath: string;
  period: SnapshotPeriod;
}) => {
  const t = useSnapshotTokens();
  const COLUMNS = columnsFor(period);
  const [sortKey, setSortKey] = useState<SortKey>("wos");
  const [sortAsc, setSortAsc] = useState(true);

  const sorted = useMemo(() => {
    const copy = [...locations];
    copy.sort((a, b) => {
      const va = sortValue(a, sortKey);
      const vb = sortValue(b, sortKey);
      if (va == null && vb == null) {
        return 0;
      }
      if (va == null) {
        return 1;
      }
      if (vb == null) {
        return -1;
      }
      const cmp =
        typeof va === "string"
          ? va.localeCompare(String(vb))
          : (va as number) - (vb as number);
      return sortAsc ? cmp : -cmp;
    });
    return copy;
  }, [locations, sortKey, sortAsc]);

  const totals = useMemo(() => {
    const sum = (get: (l: SnapshotLocation) => number | null): number | null => {
      const vals = locations.map(get).filter((v): v is number => v != null);
      return vals.length ? vals.reduce((a, b) => a + b, 0) : null;
    };
    const units = sum((l) => l.units);
    const value = sum((l) => l.value);
    const weeklyOut = sum((l) => l.weeklyOut);
    const atRisk = sum((l) => l.atRisk);
    const inboundUnits = sum((l) => l.inbound) ?? inbound.units;
    // Network WOS: demand-weighted — total on hand over total weekly outflow.
    const wos =
      units != null && weeklyOut != null && weeklyOut > 0
        ? units / weeklyOut
        : null;
    return { units, value, atRisk, inboundUnits, wos };
  }, [locations, inbound.units]);

  const th: React.CSSProperties = {
    textAlign: "right",
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: t.ink4,
    padding: "11px 10px 7px",
    borderBottom: `1px solid ${t.line}`,
    whiteSpace: "nowrap",
    cursor: "pointer",
    userSelect: "none",
  };
  const td: React.CSSProperties = {
    fontSize: 12,
    color: t.ink2,
    padding: "8px 10px",
    borderBottom: `1px solid ${t.line2}`,
    whiteSpace: "nowrap",
    textAlign: "right",
  };

  const deltaColor = (off: number | null): string => {
    if (off == null) {
      return t.ink4;
    }
    if (Math.abs(off) < 0.5) {
      return t.ink3;
    }
    return off < 0 ? t.redInk : t.orangeInk;
  };
  const inStockColor = (v: number | null): string => {
    if (v == null) {
      return t.ink4;
    }
    return v < 90 ? t.redInk : v < 94 ? t.orangeInk : t.greenInk;
  };

  return (
    <SnapCard pad={0} style={{ overflow: "hidden" }}>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {COLUMNS.map((c, i) => (
                <th
                  key={c.key}
                  style={{
                    ...th,
                    textAlign: i === 0 ? "left" : "right",
                    paddingLeft: i === 0 ? 14 : 10,
                  }}
                  onClick={() => {
                    if (sortKey === c.key) {
                      setSortAsc(!sortAsc);
                    } else {
                      setSortKey(c.key);
                      setSortAsc(true);
                    }
                  }}
                  title={`Sort by ${c.label}`}
                >
                  {c.label}
                  {sortKey === c.key ? (sortAsc ? " ↑" : " ↓") : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((d) => {
              const off =
                d.displayWos != null && d.displayTargetWos != null
                  ? d.displayWos - d.displayTargetWos
                  : null;
              const weeklyOff =
                d.wos != null && d.targetWos != null
                  ? d.wos - d.targetWos
                  : null;
              return (
                <tr key={d.id}>
                  <td style={{ ...td, textAlign: "left", paddingLeft: 14 }}>
                    <span style={{ fontWeight: 600, color: t.ink }}>
                      {d.name}
                    </span>
                    {d.id !== d.name && (
                      <span
                        style={{ fontSize: 11, color: t.ink4, marginLeft: 8 }}
                      >
                        {d.id}
                      </span>
                    )}
                  </td>
                  <td style={td}>{d.units == null ? EM_DASH : un(d.units)}</td>
                  <td style={td}>
                    {d.value == null ? EM_DASH : usd(d.value, 2)}
                  </td>
                  <td style={td}>
                    {d.displayWos == null ? EM_DASH : d.displayWos.toFixed(1)}
                  </td>
                  <td style={{ ...td, color: t.ink4 }}>
                    {d.displayTargetWos == null
                      ? EM_DASH
                      : d.displayTargetWos.toFixed(1)}
                  </td>
                  <td
                    style={{
                      ...td,
                      fontWeight: 600,
                      color: deltaColor(weeklyOff),
                    }}
                  >
                    {off == null ? EM_DASH : fmtSigned(off)}
                  </td>
                  <td
                    style={{
                      ...td,
                      fontWeight: 600,
                      color: inStockColor(d.inStock),
                    }}
                  >
                    {d.inStock == null ? EM_DASH : d.inStock.toFixed(1) + "%"}
                  </td>
                  <td style={td}>
                    {d.inbound == null ? EM_DASH : un(d.inbound)}
                  </td>
                  <td style={{ ...td, color: t.ink3 }}>
                    {d.nextArrival ?? EM_DASH}
                  </td>
                  <td style={td}>
                    {d.atRisk == null ? (
                      EM_DASH
                    ) : (
                      <LinkVal
                        to={inventoryHealthPath}
                        label="Inventory Health"
                      >
                        <span
                          style={{
                            fontWeight: 600,
                            color: d.atRisk > 15 ? t.redInk : t.ink2,
                          }}
                        >
                          {d.atRisk}
                        </span>
                      </LinkVal>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr style={{ borderTop: `2px solid ${t.ink5}` }}>
              <td
                style={{
                  ...td,
                  textAlign: "left",
                  paddingLeft: 14,
                  fontWeight: 700,
                  color: t.ink,
                  borderTop: `2px solid ${t.ink5}`,
                }}
              >
                Network total
              </td>
              <td style={{ ...td, fontWeight: 700, borderTop: `2px solid ${t.ink5}` }}>
                {totals.units == null ? EM_DASH : un(totals.units)}
              </td>
              <td style={{ ...td, fontWeight: 700, borderTop: `2px solid ${t.ink5}` }}>
                {totals.value == null ? EM_DASH : usd(totals.value, 2)}
              </td>
              <td style={{ ...td, fontWeight: 700, borderTop: `2px solid ${t.ink5}` }}>
                {totals.wos == null ? EM_DASH : totals.wos.toFixed(1)}
              </td>
              <td style={{ ...td, color: t.ink4, borderTop: `2px solid ${t.ink5}` }}>
                {EM_DASH}
              </td>
              <td style={{ ...td, borderTop: `2px solid ${t.ink5}` }}>{EM_DASH}</td>
              <td style={{ ...td, borderTop: `2px solid ${t.ink5}` }}>{EM_DASH}</td>
              <td style={{ ...td, fontWeight: 700, borderTop: `2px solid ${t.ink5}` }}>
                {totals.inboundUnits == null ? EM_DASH : un(totals.inboundUnits)}
              </td>
              <td style={{ ...td, color: t.ink3, borderTop: `2px solid ${t.ink5}` }}>
                {inbound.nextArrival ?? EM_DASH}
              </td>
              <td
                style={{
                  ...td,
                  fontWeight: 700,
                  color: t.redInk,
                  borderTop: `2px solid ${t.ink5}`,
                }}
              >
                {totals.atRisk == null ? EM_DASH : totals.atRisk}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </SnapCard>
  );
};
